import { useState } from 'react';
import { formatBytes } from '@packages/format';
import { MimeTypeIcon } from './MimeTypeIcon.js';
import { TextButton } from './TextLink.js';
import { useToast } from './Toast.js';

import { XIcon } from './XIcon';

interface SupportingDocumentRowProps {
  document: { filename: string; mimeType: string; sizeBytes: number };
  /** Opens or downloads the document. A rejection is shown as an error toast. */
  onOpen: () => Promise<void>;
  /**
   * Removes the document from this record only; the file stays with the practice,
   * so the control is a muted cross rather than a bin. Omit it for a viewer
   * who cannot manage documents.
   */
  onRemoveRequested?: () => void;
}

/** One attached supporting document in a list: filename (opens it), size, and remove. */
export function SupportingDocumentRow({ document, onOpen, onRemoveRequested }: SupportingDocumentRowProps) {
  const { addToast } = useToast();
  const [isDownloading, setIsDownloading] = useState(false);

  async function handleOpen() {
    setIsDownloading(true);
    try {
      await onOpen();
    } catch (err) {
      addToast({
        variant: 'error',
        title: "Couldn't open document",
        description: err instanceof Error ? err.message : 'Try again.',
      });
    } finally {
      setIsDownloading(false);
    }
  }

  return (
    <li className="flex items-center gap-2 rounded-md border border-border bg-bg-muted px-3 py-2 text-sm">
      <span className="shrink-0 text-text-muted">
        <MimeTypeIcon mimeType={document.mimeType} className="h-4 w-4" />
      </span>
      <TextButton
        onClick={handleOpen}
        disabled={isDownloading}
        className="min-w-0 flex-1 truncate text-left"
        title={`Open ${document.filename}`}
      >
        {document.filename}
      </TextButton>
      <span className="shrink-0 text-xs text-text-muted">{formatBytes(document.sizeBytes)}</span>
      {onRemoveRequested && (
        <button
          type="button"
          onClick={onRemoveRequested}
          className="-m-1.5 shrink-0 rounded p-1.5 text-text-muted hover:text-secondary focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary"
          aria-label={`Remove ${document.filename}`}
          title="Remove"
        >
          <XIcon className="h-3.5 w-3.5" />
        </button>
      )}
    </li>
  );
}
